import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  Cart as CartConfig,
  useLocalStorage,
  UserLocale,
} from 'hooks/userPreferences';
import { ShopifyService } from 'services/shopify';
import type { Cart } from 'types/cart/Cart';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

export const CART_KEY = 'cart';

type ShopifyCartQueryKey = [string, string];

export async function fetchCart(
  { queryKey }: QueryFunctionContext<ShopifyCartQueryKey>,
  getCartId: () => { id?: string } | null,
): Promise<Cart | null> {
  const [, countryCode] = queryKey;
  const { id: cartId } = getCartId() || {};

  if (cartId) {
    const { cart } = await ShopifyService.getCart({
      cartId,
      country: convertToCountryCodeEnum(countryCode),
    });

    if (cart) {
      return mapShopifyCart(cart);
    }
  }

  return null;
}

export const useCart = () => {
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);
  const { getValue } = useLocalStorage(CartConfig);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    queryKey: [CART_KEY, countryCode] as ShopifyCartQueryKey,
    queryFn: (context) => fetchCart(context, getValue),
    enabled: !isLocaleLoading && !!getValue(),
  });
};
